import React, { useEffect, useState, useMemo } from 'react';
import api from '../services/api';
import '../css/requisition-app-styles.css';
import { Link } from 'react-router-dom';
import TopBarIcons from './TopBarIcons';
import axios from 'axios';
import { useLocation, useNavigate } from "react-router-dom";
import "../css/Requisitions.css";
import Sidebar from './Sidebar';
import { jwtDecode } from 'jwt-decode';

const StaffSurrenders = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [surrenders, setSurrenders] = useState([]);
  const theUserType = localStorage.getItem('userType');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const theUserId = localStorage.getItem('userId');
  
  // Search and pagination state
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);

  useEffect(() => {
    fetchSurrenders();
  }, []);

  const fetchSurrenders = async () => {
    try {
      setLoading(true);
      setError(null);
      const token = localStorage.getItem("token");
      
      // Fetch all surrenders
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/Surrender`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      let data = Array.isArray(response.data)
        ? response.data
        : response.data.$values ?? [];

      console.log("Fetched surrenders:", data); // Debug log

      setSurrenders(data);
      
      if (data.length === 0) {
        setError('No surrenders found.');
      }
    } catch (err) {
      console.error("Error fetching surrenders:", err);
      if (err.response?.status === 401) {
        setError("You must be logged in to view surrenders.");
      } else {
        setError("Error. Couldn't display surrenders. Please try again.");
      }
      setSurrenders([]);
    } finally {
      setLoading(false);
    }
  };

  // Handle search change
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  // Filter surrenders based on search term
  const filterSurrenders = (surrendersList) => {
    if (!searchTerm.trim()) return surrendersList;
    
    const searchLower = searchTerm.toLowerCase();
    return surrendersList.filter(surrender => {
      // Search in Staff Name
      if (surrender.staffName && surrender.staffName.toLowerCase().includes(searchLower)) {
        return true;
      }
      // Search in Activity Name
      if (surrender.activityName && surrender.activityName.toLowerCase().includes(searchLower)) {
        return true;
      }
      // Search in Status
      if (surrender.status && surrender.status.toLowerCase().includes(searchLower)) {
        return true;
      }
      // Search in Requisition ID
      if (surrender.requisitionID && surrender.requisitionID.toLowerCase().includes(searchLower)) {
        return true;
      }
      return false;
    });
  };

  // Get paginated surrenders
  const getPaginatedSurrenders = (filteredList) => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredList.slice(startIndex, endIndex);
  };

  // Calculate Cost Drivers Total from the original requisition's cost drivers
  // This should come from the related Requisition, but since we're in Surrender,
  // we need to calculate from the actuals
  const calculateCostDriversTotal = (surrender) => {
    let total = 0;
    
    // Add regular Cost Driver Actuals
    if (surrender.costDriverActuals && surrender.costDriverActuals.length > 0) {
      total += surrender.costDriverActuals.reduce((sum, driver) => sum + (parseFloat(driver.actualSpent) || 0), 0);
    }
    
    // Add Blasting Cost Driver Actuals
    if (surrender.blastingCostDriverActuals && surrender.blastingCostDriverActuals.length > 0) {
      total += surrender.blastingCostDriverActuals.reduce((sum, driver) => sum + (parseFloat(driver.actualSpent) || 0), 0);
    }
    
    return total;
  };

  // Calculate Actual Spent from surrender
  const calculateActualSpent = (surrender) => {
    let total = 0;
    
    // Regular cost driver actuals
    if (surrender.costDriverActuals && surrender.costDriverActuals.length > 0) {
      total += surrender.costDriverActuals.reduce((sum, driver) => sum + (parseFloat(driver.actualSpent) || 0), 0);
    }
    
    // Blasting cost driver actuals
    if (surrender.blastingCostDriverActuals && surrender.blastingCostDriverActuals.length > 0) {
      total += surrender.blastingCostDriverActuals.reduce((sum, driver) => sum + (parseFloat(driver.actualSpent) || 0), 0);
    }
    
    return total;
  };

  const getStatusClass = (status) => {
    if (status === "Draft") return "status-draft";
    if (status === "Submitted") return "status-submitted";
    if (status === "Surrender Submitted") return "status-surrender-submitted";
    if (status.startsWith("Reviewed by")) return "status-reviewed";
    if (status === "Approved") return "status-approved";
    if (status === "Surrender Approved") return "status-surrender-approved";
    if (status === "Disapproved") return "status-disapproved";
    if (status === "Disbursed") return "status-disbursed";
    if (status === "To be disbursed") return "status-to-be-disbursed";
    if (status === "Surrender Review Process Complete") return "status-surrender-review-process-complete";
    return "";
  };

  // Pagination Controls Component
  const PaginationControls = ({ totalItems, itemsPerPage, currentPage, onPageChange }) => {
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    
    if (totalPages <= 1) return null;
    
    return (
      <div className="d-flex justify-content-between align-items-center mt-3">
        <div>
          Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} surrenders
          {searchTerm && <span className="text-muted ml-2">(filtered from {surrenders.length} total)</span>}
        </div>
        <nav>
          <ul className="pagination">
            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
              <button className="page-link" onClick={() => onPageChange(currentPage - 1)}>
                Previous
              </button>
            </li>
            {[...Array(totalPages)].map((_, i) => (
              <li key={i} className={`page-item ${currentPage === i + 1 ? 'active' : ''}`}>
                <button className="page-link" onClick={() => onPageChange(i + 1)}>
                  {i + 1}
                </button>
              </li>
            ))}
            <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
              <button className="page-link" onClick={() => onPageChange(currentPage + 1)}>
                Next
              </button>
            </li>
          </ul>
        </nav>
      </div>
    );
  };

  return (
    <div id="wrapper">
      <Sidebar userType={theUserType} />

      <div id="content-wrapper" className="d-flex flex-column">
        <div id="content">
          <nav className="navbar navbar-expand navbar-light bg-white topbar mb-4 static-top shadow">
            <button id="sidebarToggleTop" className="btn btn-link d-md-none rounded-circle mr-3">
              <i className="fa fa-bars"></i>
            </button>

            <form className="d-none d-sm-inline-block form-inline mr-auto ml-md-3 my-2 my-md-0 mw-100 navbar-search">
              <div className="input-group">
                <input type="text" className="form-control bg-light border-0 small" placeholder="Search"
                  aria-label="Search" aria-describedby="basic-addon2" />
                <div className="input-group-append">
                  <button className="btn btn-primary" type="button">
                    <i className="fas fa-search fa-sm"></i>
                  </button>
                </div>
              </div>
            </form>

            <ul className="navbar-nav ml-auto">
              <TopBarIcons />
            </ul>
          </nav>

          <div className="container-fluid">
            {/* Header Row with Search Bar */}
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div>
                <h1 className="h3 mb-0">Staff Surrenders</h1>
              </div>
              <div className="d-flex align-items-center">
                <div className="input-group" style={{ width: '300px' }}>
                  <input 
                    type="text" 
                    className="form-control" 
                    placeholder="Search" 
                    value={searchTerm}
                    onChange={handleSearchChange}
                  />
                  <div className="input-group-append">
                    <button className="btn-outline-secondary round-right-corners" type="button">
                      <i className="fas fa-search"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {loading && (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" style={{width: '3rem', height: '3rem'}} role="status">
                  <span className="sr-only">Loading...</span>
                </div>
                <p className="mt-3 text-muted">Loading surrenders...</p>
              </div>
            )}

            {error && !loading && (
              <div className="alert alert-warning mt-3" role="alert">
                {error}
              </div>
            )}

            {!loading && (
              <div id="surrender-table" className="table-responsive mt-4">
                <table className="table table-hover table-striped table-bordered text-sm align-middle">
                  <thead className="thead-dark">
                    <tr>
                      <th className="text-nowrap">Staff Name</th>
                      <th className="text-nowrap">Activity Name</th>
                      <th className="text-nowrap">Cost Drivers Total</th>
                      <th className="text-nowrap">Actual Spent</th>
                      <th className="text-nowrap">Status</th>
                      <th className="text-nowrap">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      const filtered = filterSurrenders(surrenders);
                      const sorted = [...filtered].sort((a, b) => new Date(b.surrenderDate) - new Date(a.surrenderDate));
                      const paginated = getPaginatedSurrenders(sorted);
                      
                      if (paginated.length === 0) {
                        return (
                          <tr>
                            <td colSpan="6" className="text-center text-muted py-4">
                              {searchTerm 
                                ? `"${searchTerm}" not found in surrenders`
                                : 'No surrenders found.'}
                            </td>
                          </tr>
                        );
                      }
                      
                      return paginated.map((surrender) => {
                        const costDriversTotal = calculateCostDriversTotal(surrender);
                        const actualSpent = calculateActualSpent(surrender);
                        
                        console.log("Surrender data:", surrender); // Debug log
                        
                        return (
                          <tr key={surrender.requisitionID}>
                            <td>{surrender.staffName || 'N/A'}</td>
                            <td>{surrender.activityName || 'N/A'}</td>
                            <td>MK {costDriversTotal.toLocaleString()}</td>
                            <td>MK {actualSpent.toLocaleString()}</td>
                            <td>
                              <div className={`badge ${getStatusClass(surrender.status)}`}>
                                {surrender.status || 'N/A'}
                              </div>
                            </td>
                            <td>
                              <div className="d-flex gap-2 flex-wrap">
                                <Link to={`/review-surrender/${surrender.requisitionID}`}>
                                  <button
                                    className="btn btn-outline-primary btn-sm"
                                  >
                                    <i className="fas fa-file-alt me-1"></i> Review
                                  </button>
                                </Link>
                              </div>
                            </td>
                          </tr>
                        );
                      });
                    })()}
                  </tbody>
                </table>

                {filterSurrenders(surrenders).length > itemsPerPage && (
                  <PaginationControls
                    totalItems={filterSurrenders(surrenders).length}
                    itemsPerPage={itemsPerPage}
                    currentPage={currentPage}
                    onPageChange={setCurrentPage}
                  />
                )}
              </div>
            )}
          </div>
        </div>

        <footer className="sticky-footer bg-white">
          <div className="container my-auto">
            <div className="copyright text-center my-auto">
              <span>Copyright &copy; Farm Concern International 2025</span>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default StaffSurrenders;